import React, { useRef, useState } from 'react';
import type { ReactElement } from 'react';
import styled from 'styled-components';
import type { SelectOption } from '@construct-kit/core';
import {
  MultiSelect,
  Inline,
  paletteHelper,
  typographyHelper,
  spacingHelper,
} from '@construct-kit/core';
import {
  DEFAULT_SELECT_PROPERTY_OPTION,
  propertyTypesForSelectElement,
} from '../../../shared/model/constants/propertyTypes';
import { trackSearchRefinementModalOpen } from '../../../shared/tracking/tealium/tracking';
import type { SelectedFiltersOptionsType } from '../../../shared/model/constants/filterTypes';

export type PropertyTypesDropdownFilterProps = {
  selectedFilterOptions: SelectedFiltersOptionsType;
  setSelectedFilterOptionsHomepage: (_: SelectedFiltersOptionsType) => void;
};

const PropertyTypesSelect = styled(MultiSelect)`
  width: 13.5rem;
  margin-right: ${spacingHelper('extraSmall')};
  border: none;
  border-style: none;
  ${typographyHelper('body01')};

  .inputBorder::after {
    border: none;
  }

  :focus-within {
    box-shadow: 0 -1px 16px rgba(0, 0, 0, 0.16);
    border-radius: ${({ theme }) => theme.border.radius.medium};
  }

  .scrollableArea:hover {
    border-radius: ${({ theme }) => theme.border.radius.medium};
    background-color: ${paletteHelper('backgroundSecondaryBase')};
  }

  .listbox {
    border: none;
    border-top: 1px solid ${paletteHelper('borderSecondary')};
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.16);
  }

  li {
    white-space: normal;
    position: relative;
    padding-left: ${spacingHelper('twoExtraLarge')};

    > * > div {
      position: absolute;
      left: ${spacingHelper('medium')};
    }
  }
`;

const VerticalDivider = styled.div`
  border-left: 0.0625rem solid ${paletteHelper('borderSecondary')};
  height: 3rem;
`;

const PropertyDropdownWrapper = styled.div`
  padding: 0;
  margin: 0;
`;

export const PropertyTypesDropdownFilter = ({
  selectedFilterOptions,
  setSelectedFilterOptionsHomepage,
}: PropertyTypesDropdownFilterProps): ReactElement => {
  const [selectedDropdownPropertyTypes, setSelectedDropdownPropertyTypes] = useState<
    SelectOption[]
  >([DEFAULT_SELECT_PROPERTY_OPTION]);

  const ref = useRef<HTMLInputElement>(null);

  const propertyTypeSelectArray = [
    DEFAULT_SELECT_PROPERTY_OPTION,
    ...propertyTypesForSelectElement,
  ];

  const renderDisplayText = (selectedOptions: SelectOption[]) => {
    if (selectedOptions.length === 1) return selectedOptions[0].label;
    return `${selectedOptions.length} property types`;
  };

  const handlePropertyMultiselect = (selectedOptions: SelectOption[]) => {
    if (
      selectedOptions.length === 0 ||
      (selectedOptions.includes(DEFAULT_SELECT_PROPERTY_OPTION) &&
        selectedOptions[0].value !== 'all')
    ) {
      setSelectedDropdownPropertyTypes([propertyTypeSelectArray[0]]);
      setSelectedFilterOptionsHomepage({ ...selectedFilterOptions, propertyTypes: [] });
      return;
    }

    const propertyTypes = selectedOptions.reduce((accPropertyTypes: string[], option, _) => {
      if (option.value === 'all') return accPropertyTypes;
      return [...accPropertyTypes, option.value];
    }, []);
    setSelectedDropdownPropertyTypes(selectedOptions.filter((option) => option.value !== 'all'));
    setSelectedFilterOptionsHomepage({ ...selectedFilterOptions, propertyTypes });
  };

  return (
    <Inline grow={false} gap="0">
      <PropertyDropdownWrapper
        onClick={() => {
          // eslint-disable-next-line no-unused-expressions
          (ref.current?.ariaExpanded === 'false' || ref.current?.ariaExpanded === undefined) &&
            trackSearchRefinementModalOpen({
              searchMode: 'search',
              userAction: 'property-type-dropdown',
            });
        }}
      >
        <PropertyTypesSelect
          ref={ref}
          onSelectedOptionsChange={handlePropertyMultiselect}
          options={propertyTypeSelectArray}
          defaultSelectedOptions={[propertyTypeSelectArray[0]]}
          selectedOptions={selectedDropdownPropertyTypes}
          hideLabel
          label="property types"
          placeholder="All property types"
          renderDisplayText={renderDisplayText}
          dangerouslySetClassNames={{
            inputBorder: 'inputBorder',
            scrollableArea: 'scrollableArea',
            listbox: 'listbox',
          }}
          listboxMaxHeight="17rem"
        />
      </PropertyDropdownWrapper>
      <VerticalDivider />
    </Inline>
  );
};

export default PropertyTypesDropdownFilter;
